/* ============================================================================
 * Sales Report → Expense Details → Fixed Expense (the fourth tab, brief §9.2).
 *
 * Built from the same parts as the Stock-In, Stock-Out and NC tabs — the same summary cards,
 * search box and filter dropdown, record cards, badges, loading/error/empty states — and fed by
 * the Net Profit Analyzer's ledger through /api/net-profit/fixed-share, so a full month here
 * equals the Analyzer's operating expenses plus tax and interest, rupee for rupee.
 * ========================================================================== */
import React, { useMemo, useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faLock } from '@fortawesome/free-solid-svg-icons';
import { StandardSearch } from '../../styles/standardStyles';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const rs = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const rs2 = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLong = (key) => (key ? `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(0, 4)}` : '');
const monthOnly = (key) => (key ? MONTHS[Number(key.slice(5, 7)) - 1] : '');
const dayShort = (d) => (d ? `${Number(d.slice(8, 10))} ${SHORT[Number(d.slice(5, 7)) - 1]}` : '');
const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

const badge = (bg, color) => ({ background: bg, color, padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' });
const BEHAVIOR_BADGE = {
  salary: badge('#e0e7ff', '#4338ca'), fixed: badge('#dbeafe', '#1e40af'), variable: badge('#e0e7ff', '#4338ca'),
  volume: badge('#dbeafe', '#1e40af'), spread: badge('#e0e7ff', '#4338ca'), one_off: badge('#dbeafe', '#1e40af'),
};
const STATUS_BADGE = {
  estimated: [badge('#fef3c7', '#92400e'), 'Estimated'],
  confirmed: [badge('#dbeafe', '#1e40af'), 'Confirmed'],
  locked: [badge('#f3f4f6', '#374151'), 'Locked'],
};
const PAY_BADGE = {
  paid: [badge('#dcfce7', '#15803d'), 'Paid'],
  part: [badge('#fef3c7', '#92400e'), 'Part paid'],
  unpaid: [badge('#fee2e2', '#dc2626'), 'Not paid'],
  overdue: [badge('#fee2e2', '#dc2626'), 'Overdue'],
};

/** The card's detail line, part by part (one part per month the period touches). */
function detailLine(card) {
  if (card.masked) return card.role || 'Staff';
  return (card.parts || []).map((p) => {
    if (card.sourceKind === 'worker') {
      return `${card.role ? `${card.role} · ` : ''}${monthOnly(p.periodKey)} cost ${rs(p.monthCost)} · Paid ${rs(p.paid)} · Due ${rs(p.due)}${p.type === 'days' && p.days !== p.daysInMonth ? ` · ${p.days} of ${p.daysInMonth} days in range` : ''}`;
    }
    if (p.type === 'oneoff') return `One-time · ${dayShort(p.date)}`;
    if (p.type === 'late') return `Posted ${dayShort(p.date)}${p.forPeriodKey ? ` · for ${monthOnly(p.forPeriodKey)}` : ''}`;
    if (p.type === 'sales') return `${Number(p.orders || 0).toLocaleString('en-IN')} orders · net sales ${rs(p.netSales)} in range`;
    if (p.type === 'days') {
      return p.days === p.daysInMonth ? `Full month ${rs(p.monthCost)}` : `Full month ${rs(p.monthCost)} · ${p.days} of ${p.daysInMonth} days in range`;
    }
    return '';
  }).filter(Boolean).join('  |  ');
}

export function filterShare(share, query, field) {
  if (!share || !share.available) return { groups: [], total: 0, records: 0, paid: 0, due: 0, searching: false };
  const words = norm(query).split(' ').filter(Boolean);
  if (!words.length) return { ...share, searching: false };
  const hit = (c) => {
    const src = norm(field === 'category' ? `${c.category} ${c.behaviorLabel}` : c.name);
    return words.every((w) => src.includes(w));
  };
  const groups = share.groups.map((g) => {
    const cards = g.cards.filter(hit);
    return { ...g, cards, count: cards.length, total: g.masked ? null : cards.reduce((s, c) => s + (c.amount || 0), 0) };
  }).filter((g) => g.count > 0);
  const visible = groups.flatMap((g) => g.cards).filter((c) => !c.masked);
  return {
    ...share, groups, searching: true,
    total: visible.reduce((s, c) => s + c.amount, 0),
    paid: visible.reduce((s, c) => s + c.paid, 0),
    due: visible.reduce((s, c) => s + c.due, 0),
    records: groups.reduce((s, g) => s + g.count, 0),
    estimated: visible.some((c) => c.estimated),
  };
}

/** Summary cards + search row, in the window header (same parts as the Stock-In tab). */
export function FixedExpenseHeader({ view, query, setQuery, field, setField }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const card = { flex: '1', background: '#fff', padding: '16px 20px', borderRadius: '8px', color: '#000000', border: '1px solid #e5e7eb' };
  const loaded = view && view.available;
  return (<>
    <div className="expense-summary-stats" style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
      <div className="expense-summary-card" style={card}>
        <div className="expense-summary-label" style={{ fontSize: '14px', color: '#6b7280' }}>Total Expenses</div>
        <div className="expense-summary-value" style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '8px', color: '#000000' }}>
          {loaded ? rs2(view.total) : '-'}
          {loaded && view.estimated && <span style={{ fontSize: '12px', color: '#92400e', fontWeight: 500, marginLeft: 6 }}>est.</span>}
        </div>
        {loaded && view.excludesSalaries && (view.groups || []).some((g) => g.key === 'salaries') && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>excl. staff salaries</div>
        )}
      </div>
      <div className="expense-summary-card" style={card}>
        <div className="expense-summary-label" style={{ fontSize: '14px', color: '#6b7280' }}>Total Records</div>
        <div className="expense-summary-value" style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '8px', color: '#000000' }}>{loaded ? view.records : 0}</div>
        {loaded && view.records > 0 && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Paid {rs(view.paid)} · Due {rs(view.due)}</div>}
      </div>
    </div>
    <div className="expense-search-filter-row">
      <div className="expense-search-wrapper">
        <StandardSearch
          value={query}
          onChange={setQuery}
          onClear={() => setQuery('')}
          placeholder={field === 'name' ? 'Search names...' : 'Search categories...'}
          showClearButton
          style={{ marginBottom: '0', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', height: '44px', padding: '0 0 0 16px' }}
        />
      </div>
      <div className="expense-dropdown-filter-container" ref={ref}>
        <button type="button" className="expense-dropdown-filter-btn" onClick={() => setOpen((x) => !x)} title="Filter search by">
          <span className="expense-dropdown-filter-value">{field === 'name' ? 'Name' : 'Category'}</span>
          <FontAwesomeIcon icon={faAngleDown} className="expense-dropdown-arrow" />
        </button>
        {open && (
          <div className="expense-dropdown-menu">
            {['name', 'category'].map((f) => (
              <button key={f} type="button" className={`expense-dropdown-option ${field === f ? 'active' : ''}`} onClick={() => { setField(f); setOpen(false); }}>
                {f === 'name' ? 'Name' : 'Category'}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  </>);
}

const centered = { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: '100%', textAlign: 'center' };

/** The window body: loading, error with Retry, empty state, or the grouped cards. */
export function FixedExpenseBody({ state, view, query, field, onRetry, onOpenCard, onOpenGroup, onSetup }) {
  const groups = useMemo(() => (view && view.groups) || [], [view]);
  if (state.status === 'loading' && !state.data) {
    return <div style={{ ...centered, color: '#9ca3af' }}><p style={{ fontSize: '16px' }}>Loading expense details...</p></div>;
  }
  if (state.status === 'error') {
    return (
      <div style={{ ...centered, color: '#b91c1c', flexDirection: 'column', gap: '12px' }}>
        <p style={{ fontSize: '16px', margin: 0 }}>❌ {state.error}</p>
        <button type="button" onClick={onRetry} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #111111', background: '#111111', color: '#ffffff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>Retry</button>
      </div>
    );
  }
  const canSetup = !!(state.data && state.data.viewer && state.data.viewer.canEdit);
  if (!groups.length) {
    const share = state.data && state.data.share;
    return (
      <div style={{ ...centered, transform: 'translateY(-8%)', color: '#9ca3af', flexDirection: 'column', gap: 12 }}>
        <p style={{ fontSize: '16px', margin: 0 }}>
          {query && query.trim() ? `No fixed expense records found for ${field} "${query.trim()}"` : 'No fixed expense records found for this period'}
        </p>
        {share && share.beforeStart && share.startsFrom && <p style={{ fontSize: 13, margin: 0 }}>Fixed expenses start from {monthLong(share.startsFrom)}</p>}
        {state.data && state.data.installed === false && <p style={{ fontSize: 13, margin: 0 }}>Fixed expenses are not set up on this server yet</p>}
        {canSetup && !(query && query.trim()) && (
          <button type="button" onClick={onSetup} style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #111111', background: '#111111', color: '#ffffff', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
            Set up fixed expenses
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="expense-records-scroll-area" style={{ display: 'flex', flexDirection: 'column', gap: '12px', minHeight: '100%' }}>
      {view.beforeStart && view.startsFrom && (
        <div style={{ fontSize: 12, color: '#6b7280', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
          Fixed expenses start from {monthLong(view.startsFrom)}; earlier days in this period carry none.
        </div>
      )}
      {groups.map((g) => (
        <div key={g.key} className="expense-record-card" style={{ border: '1px solid #e5e7eb', borderRadius: '12px', padding: '16px', background: '#fff' }}>
          <div className="expense-record-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
            <div className="expense-badges" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span className="expense-price" style={{ fontSize: '1.125rem', fontWeight: 600, color: '#000000' }}>{g.masked ? 'Hidden' : rs2(g.total)}</span>
              <span
                style={{ ...badge('#f3f4f6', '#111827'), cursor: g.key === 'salaries' ? 'pointer' : 'default', textDecoration: g.key === 'salaries' ? 'underline' : 'none' }}
                onClick={g.key === 'salaries' ? () => onOpenGroup(g) : undefined}
                title={g.key === 'salaries' ? 'Open Staff salaries' : undefined}
              >
                {g.name}{g.key === 'salaries' ? ' ›' : ''}
              </span>
            </div>
            <div className="expense-date" style={{ fontSize: '14px', color: '#6b7280' }}>{g.count} record{g.count === 1 ? '' : 's'}</div>
          </div>
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {g.cards.map((c) => {
              const [stStyle, stLabel] = STATUS_BADGE[c.status] || STATUS_BADGE.confirmed;
              const pay = PAY_BADGE[c.paymentStatus];
              return (
                <div key={c.id} style={{ display: 'flex', flexDirection: 'column', padding: '12px', background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: 1 }}>
                      <span style={{ color: '#000000', fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.name}{c.sourceKind === 'worker' && c.role ? <span style={{ color: '#6b7280', fontWeight: 400 }}> · {c.role}</span> : null}
                      </span>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={BEHAVIOR_BADGE[c.behavior] || BEHAVIOR_BADGE.fixed}>{c.behaviorLabel}</span>
                        <span style={stStyle}>{stLabel}</span>
                        {pay && !c.masked && c.behavior !== 'volume' && <span style={pay[0]}>{pay[1]}</span>}
                        {c.billPhotoUrl && (
                          <span style={{ ...badge('#fef3c7', '#92400e'), fontSize: '11px', cursor: 'pointer' }} onClick={() => window.open(c.billPhotoUrl, '_blank')}>📄 Bill Available</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                      <span className="expense-price" style={{ fontSize: '1.125rem', fontWeight: 600, color: '#000000' }}>{c.masked ? '—' : rs2(c.amount)}</span>
                      <button
                        type="button"
                        onClick={() => onOpenCard(c)}
                        title={c.locked ? 'This month is closed — opens read-only' : 'Open in Expenses'}
                        style={c.locked
                          ? { background: '#f3f4f6', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }
                          : { background: '#111111', color: '#ffffff', border: '1px solid #111111', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}
                      >
                        {c.locked && <FontAwesomeIcon icon={faLock} />}{c.locked ? 'View in Expenses ›' : 'Open in Expenses ›'}
                      </button>
                    </div>
                  </div>
                  <div style={{ paddingTop: '8px', borderTop: '1px solid #f3f4f6', fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>{detailLine(c)}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
